import React, { useState } from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import Tabs, { Tab } from 'components/Tabs';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const TabContent = styled.div`
  padding: 24px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'};
  border-radius: 8px;
  margin-top: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#e2e8f0' : '#334155')};
`;

// Icons for demo
const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z" />
    <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
  </svg>
);

// eslint-disable-next-line no-empty-function
const noop = () => {};

const TabsDocs = () => {
  const [activeTab1, setActiveTab1] = useState('details');
  const [activeTab2, setActiveTab2] = useState('users');
  const [activeTab3, setActiveTab3] = useState('details');

  const basicTabs = [
    { id: 'details', label: 'Details' },
    { id: 'settings', label: 'Settings' },
    { id: 'users', label: 'Users' },
  ];

  const tabsWithCounts = [
    { id: 'users', label: 'Users', count: 12 },
    { id: 'pending', label: 'Pending', count: 3 },
    { id: 'archived', label: 'Archived', count: 45 },
  ];

  const tabsWithIcons = [
    { id: 'details', label: 'Details' },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
    { id: 'users', label: 'Users', icon: <UserIcon /> },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Tabs</Title>
        <Subtitle>
          Navigation component for switching between different views or
          sections. Supports icons, count badges, and multiple sizes.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Component Location</SectionTitle>
        <SectionDescription>
          The Tabs component is located at{' '}
          <code>/src/components/Tabs/index.js</code>
        </SectionDescription>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <SectionDescription>
          The simplest way to use Tabs is with the <code>tabs</code> array prop.
          Each tab needs an <code>id</code> and <code>label</code>.
        </SectionDescription>

        <ComponentDemo label="Basic Tabs">
          <Tabs
            tabs={basicTabs}
            activeTab={activeTab1}
            onTabChange={setActiveTab1}
          />
          <TabContent>
            Active tab: <strong>{activeTab1}</strong>
          </TabContent>
        </ComponentDemo>

        <CodeBlock>
          {`import Tabs from 'components/Tabs';

const [activeTab, setActiveTab] = useState('details');

const tabs = [
  { id: 'details', label: 'Details' },
  { id: 'settings', label: 'Settings' },
  { id: 'users', label: 'Users' },
];

<Tabs
  tabs={tabs}
  activeTab={activeTab}
  onTabChange={setActiveTab}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Tabs with Counts</SectionTitle>
        <SectionDescription>
          Add count badges to tabs to show quantities. Useful for showing number
          of items in each category.
        </SectionDescription>

        <ComponentDemo label="With Count Badges">
          <Tabs
            tabs={tabsWithCounts}
            activeTab={activeTab2}
            onTabChange={setActiveTab2}
          />
        </ComponentDemo>

        <CodeBlock>
          {`const tabs = [
  { id: 'users', label: 'Users', count: 12 },
  { id: 'pending', label: 'Pending', count: 3 },
  { id: 'archived', label: 'Archived', count: 45 },
];

<Tabs
  tabs={tabs}
  activeTab={activeTab}
  onTabChange={setActiveTab}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Tabs with Icons</SectionTitle>
        <SectionDescription>
          Add icons to tabs for better visual hierarchy. Icons are automatically
          sized based on the tab size.
        </SectionDescription>

        <ComponentDemo label="With Icons">
          <Tabs
            tabs={tabsWithIcons}
            activeTab={activeTab3}
            onTabChange={setActiveTab3}
          />
        </ComponentDemo>

        <CodeBlock>
          {`const tabs = [
  { id: 'details', label: 'Details' },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
  { id: 'users', label: 'Users', icon: <UserIcon /> },
];

<Tabs
  tabs={tabs}
  activeTab={activeTab}
  onTabChange={setActiveTab}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Tab Sizes</SectionTitle>
        <SectionDescription>
          Three size options: small, medium (default), and large.
        </SectionDescription>

        <ComponentDemo label="Small" gap="16px" direction="column">
          <Tabs
            tabs={basicTabs}
            activeTab="details"
            onTabChange={noop}
            size="small"
          />
        </ComponentDemo>

        <ComponentDemo label="Medium (Default)" gap="16px" direction="column">
          <Tabs
            tabs={basicTabs}
            activeTab="settings"
            onTabChange={noop}
            size="medium"
          />
        </ComponentDemo>

        <ComponentDemo label="Large" gap="16px" direction="column">
          <Tabs
            tabs={basicTabs}
            activeTab="users"
            onTabChange={noop}
            size="large"
          />
        </ComponentDemo>

        <CodeBlock>
          {`<Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} size="small" />
<Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} size="medium" />
<Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} size="large" />`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Children Pattern</SectionTitle>
        <SectionDescription>
          Alternatively, you can use the children pattern for more control over
          individual tabs. Import the <code>Tab</code> component and use it as
          children.
        </SectionDescription>

        <ComponentDemo label="Using Children">
          <Tabs>
            <Tab active>Active Tab</Tab>
            <Tab>Another Tab</Tab>
            <Tab count={5}>With Count</Tab>
            <Tab disabled>Disabled</Tab>
          </Tabs>
        </ComponentDemo>

        <CodeBlock>
          {`import Tabs, { Tab } from 'components/Tabs';

<Tabs>
  <Tab active>Active Tab</Tab>
  <Tab>Another Tab</Tab>
  <Tab count={5}>With Count</Tab>
  <Tab disabled>Disabled</Tab>
</Tabs>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Disabled Tabs</SectionTitle>
        <SectionDescription>
          Individual tabs can be disabled to prevent interaction.
        </SectionDescription>

        <ComponentDemo label="Disabled State">
          <Tabs
            tabs={[
              { id: 'details', label: 'Details' },
              { id: 'settings', label: 'Settings' },
              { id: 'admin', label: 'Admin', disabled: true },
            ]}
            activeTab="details"
            onTabChange={noop}
          />
        </ComponentDemo>

        <CodeBlock>
          {`const tabs = [
  { id: 'details', label: 'Details' },
  { id: 'settings', label: 'Settings' },
  { id: 'admin', label: 'Admin', disabled: true },
];`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="Tabs Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>children</strong> (node): Direct Tab components as children
            <br />
            <strong>tabs</strong> (array): Array of tab configurations
            <br />
            &nbsp;&nbsp;- <code>id</code> (string, required): Unique tab
            identifier
            <br />
            &nbsp;&nbsp;- <code>label</code> (string, required): Tab display
            text
            <br />
            &nbsp;&nbsp;- <code>icon</code> (node): Optional icon element
            <br />
            &nbsp;&nbsp;- <code>count</code> (number | string): Optional count
            badge
            <br />
            &nbsp;&nbsp;- <code>disabled</code> (boolean): Disable the tab
            <br />
            <strong>activeTab</strong> (string): Currently active tab ID
            <br />
            <strong>onTabChange</strong> (function): Callback when tab changes
            <br />
            <strong>size</strong> (string): Tab size - "small", "medium"
            (default), "large"
            <br />
            <strong>className</strong> (string): Additional CSS class
          </div>
        </Card>

        <Card headerBorder title="Tab Props" style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>children</strong> (node, required): Tab content/label
            <br />
            <strong>active</strong> (boolean): Whether tab is active
            <br />
            <strong>onClick</strong> (function): Click handler
            <br />
            <strong>icon</strong> (node): Optional icon element
            <br />
            <strong>count</strong> (number | string): Optional count badge
            <br />
            <strong>size</strong> (string): Tab size
            <br />
            <strong>disabled</strong> (boolean): Disable the tab
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              Use tabs for switching between related views or content
              <br />
              Keep tab labels short and descriptive
              <br />
              Use count badges to show quantities in each tab
              <br />
              Disable tabs that are not available in current context
              <br />
              Consider mobile responsiveness (use Select dropdown for many tabs)
            </div>
          </Card>

          <Card title="Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              Don't use too many tabs (5-7 max recommended)
              <br />
              Don't use tabs for primary navigation (use nav menu instead)
              <br />
              Don't mix tabs and breadcrumbs for the same navigation
              <br />
              Don't put long text in tab labels
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Real-World Example</SectionTitle>
        <SectionDescription>
          Complete example with tab content switching.
        </SectionDescription>

        <Card>
          <CodeBlock>
            {`import React, { useState } from 'react';
import Tabs from 'components/Tabs';

const AccountPage = () => {
  const [activeTab, setActiveTab] = useState('details');

  const TABS = [
    { id: 'details', label: 'Details' },
    { id: 'tickets', label: 'Tickets' },
    { id: 'staff', label: 'Staff' },
    { id: 'hardware', label: 'Hardware' },
    { id: 'notes', label: 'Notes' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'details':
        return <DetailsTab />;
      case 'tickets':
        return <TicketsTab />;
      case 'staff':
        return <StaffTab />;
      // ... other cases
      default:
        return null;
    }
  };

  return (
    <div>
      <Tabs
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      {renderContent()}
    </div>
  );
};`}
          </CodeBlock>
        </Card>
      </Section>
    </div>
  );
};

export default TabsDocs;
